/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-useless-escape */
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { Formik, Form, Field, FormikProps } from "formik";
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
} from "@mui/material";
import LoadingModal from "../../components/CustomModal/LoadingModal";
import DevFeeAdjModal from "./DevFeeAdjModal";
import {
  OptimizationType,
  convertPercentToDecimal,
  createValidationSchema,
  decimalPctgToString,
  formFields,
  initialItcAdders,
  resolveItcAdders,
  resolveItcAddersForQuote,
  shouldRenderInput,
  stringToEscalator,
} from "./helpers";
import { InputFormPayload } from "./types";
import DecimalTextField from "./FieldTypes/DecimalField";
import SelectorField from "./FieldTypes/SelectField";
import useOptimizeMutation, {
  QuoteWithSDDTO,
} from "../../api/useOptimizeInputs";
import { toast } from "react-toastify";
import { isFinanceUser, isDevUser } from "../../utils";

export const INITIAL_DEV_FEE = 0.369;
export const INITIAL_DEV_FEE_BESS = 90;

const CalculatorForm = ({
  action,
  prevFormData,
  selectedSystemDesign,
  opportunityState,
  setInputs,
  setOutputDestination,
  systemDesignData,
  opportunityData,
  opportunityId,
  calculatorVersion,
  setOutputs,
  setShowOutputs,
  setLastAction,
  sdIsEngineered,
  devFeeRerunRef,
}: {
  action: "view" | "edit" | "clone" | "new";
  prevFormData?: InputFormPayload;
  selectedSystemDesign: string | null;
  opportunityState: string;
  systemDesignData: any;
  opportunityData: any;
  opportunityId: string;
  calculatorVersion: string;
  setInputs: (data: any) => void;
  setOutputs: (data: any) => void;
  setShowOutputs: (show: boolean) => void;
  setLastAction: (action: string) => void;
  setOutputDestination: (destination: string) => void;
  epcComponentCosts: any[];
  sdIsEngineered: boolean;
  // Populated with a function that re-runs the Dev Fee solver against another
  // system design, using the current form inputs.
  devFeeRerunRef?: MutableRefObject<
    ((sd: { id: string; data: any }) => Promise<void>) | null
  >;
}) => {
  const [showDevFeeModal, setShowDevFeeModal] = useState(false);
  const currentFormState = useRef<typeof initialValues>();
  const formikRef = useRef<FormikProps<typeof initialValues>>(null);
  const optimizeMutation = useOptimizeMutation();

  // dollars_kwh_ppa/dollars_kwh_esa are never persisted (they're derived, UI-only
  // inputs) - re-derive them from the saved discount so they're populated when a
  // rate-mode quote is reopened, instead of showing blank.
  const rawSavedDiscount = prevFormData?.disc_to_pre_system_utility_bill_pcnt_inp;
  const savedDiscountFraction =
    rawSavedDiscount == null
      ? undefined
      : rawSavedDiscount >= 1
        ? rawSavedDiscount / 100
        : rawSavedDiscount;
  const derivedRateFromDiscount =
    prevFormData?.avoided_cost_per_kwh_qt != null &&
    savedDiscountFraction != null
      ? prevFormData.avoided_cost_per_kwh_qt * (1 - savedDiscountFraction)
      : undefined;

  const initialValues = {
    ac_system_size_estimated:
      prevFormData?.ac_system_size_estimated ||
      (prevFormData?.pv_size_dc_kw_sd || 0) *
        (prevFormData?.ac_system_size_multiplier || 1.25) ||
      0,
    ac_system_size_kw: prevFormData?.ac_system_size_kw,
    ac_system_size_multiplier: prevFormData?.ac_system_size_multiplier || 1.25,
    avoided_cost_per_kwh_qt: prevFormData?.avoided_cost_per_kwh_qt,
    carport_pv_dc_kw: systemDesignData?.carport_pv_dc_kw__c,
    custom_opp_maintain_cost_applies:
      prevFormData?.custom_opp_maintain_cost_applies || "No",
    custom_opp_maintain_cost_escal: stringToEscalator(
      prevFormData?.custom_opp_maintain_cost_escal || "0.0199",
    ),
    custom_opp_maintain_cost_yr1_per_kw:
      prevFormData?.custom_opp_maintain_cost_yr1_per_kw,
    custom_opp_maintain_cost_yr6_per_kw:
      prevFormData?.custom_opp_maintain_cost_yr6_per_kw,
    custom_useful_life_term: prevFormData?.custom_useful_life_term,
    disc_to_pre_system_utility_bill_pcnt_inp: decimalPctgToString(
      prevFormData?.disc_to_pre_system_utility_bill_pcnt_inp,
    ),
    epc_total_per_watt_inp_qt: prevFormData?.epc_total_per_watt_inp_qt,
    epc_total_per_watt_inp_sd:
      prevFormData?.epc_total_per_watt_inp_sd ||
      prevFormData?.epc_total_per_watt_inp_qt ||
      prevFormData?.epc_total_per_watt_sd,
    epc_total_per_watt_sd:
      prevFormData?.epc_total_per_watt_sd ||
      prevFormData?.epc_total_per_watt_sd_dep ||
      0,
    financing_type: prevFormData?.financing_type,
    ground_mount_pv_dc_kw: systemDesignData?.ground_mount_pv_dc_kw__c,
    itc_adder_domestic_qt: prevFormData?.itc_adder_domestic_qt || "No",
    itc_adder_energy_com_qt: prevFormData?.itc_adder_energy_com_qt || "No",
    itc_adder_low_inc_qt: prevFormData?.itc_adder_low_inc_qt || "No",
    ...initialItcAdders(
      prevFormData,
      systemDesignData?.project_type__c || "Solar-only",
      action,
    ),
    itc_ineligible_nom_qt: prevFormData?.itc_ineligible_nom_qt,
    kwh_per_kw_qt: prevFormData?.kwh_per_kw_qt,
    kwh_per_kw_sd:
      prevFormData?.kwh_per_kw_sd_dep ||
      prevFormData?.kwh_per_kw_sd ||
      prevFormData?.kwh_per_kw_qt ||
      systemDesignData?.kwh_per_kw_sd_flow__c,
    lease_escal_orig_term: stringToEscalator(
      prevFormData?.lease_escal_orig_term || "0.0199",
    ),
    lease_term_yrs_num: prevFormData?.lease_term_yrs_num || "25",
    offtaker_holds_rec_rights: prevFormData?.offtaker_holds_rec_rights || "No",
    esa_escal_orig_term: stringToEscalator(
      prevFormData?.esa_escal_orig_term || "0.0199",
    ),
    esa_prod_guar_included: prevFormData?.esa_prod_guar_included || "No",
    esa_term_yrs_num: prevFormData?.esa_term_yrs_num || "25",
    ppa_escal_orig_term: stringToEscalator(
      prevFormData?.ppa_escal_orig_term || "0.0199",
    ),
    ppa_prod_guar_included: prevFormData?.ppa_prod_guar_included || "No",
    ppa_term_yrs_num: prevFormData?.ppa_term_yrs_num || "25",
    public_entity: prevFormData?.public_entity || "No",
    public_school: prevFormData?.public_school || "No",
    pv_size_dc_kw_sd: prevFormData?.pv_size_dc_kw_sd,
    rec_group: prevFormData?.rec_group,
    rec_type: prevFormData?.rec_type,
    rooftop_pv_dc_kw: systemDesignData?.rooftop_pv_dc_kw__c,
    site_lease_annual_nom: prevFormData?.site_lease_annual_nom,
    site_lease_escal: stringToEscalator(prevFormData?.site_lease_escal),
    site_lease_included: prevFormData?.site_lease_included || "No",
    site_lease_term_inp: prevFormData?.site_lease_term_inp,
    sr_finl_dev_fee_per_watt_inp:
      prevFormData?.sr_finl_dev_fee_per_watt_inp || INITIAL_DEV_FEE,
    state_incentive_yr1_nom_qt:
      prevFormData?.state_incentive_yr1_nom_qt ||
      prevFormData?.state_incentive_yr1_nom_sd,
    state_incentive_yr1_nom_sd: prevFormData?.state_incentive_yr1_nom_sd,
    utility_name: prevFormData?.utility_name,
    community_type: "None",
    unilateral_auto_renewal: prevFormData?.unilateral_auto_renewal || "Unilateral",

    battery_energy_rating_kwh: prevFormData?.battery_energy_rating_kwh,
    battery_pwr_rating_kw: prevFormData?.battery_pwr_rating_kw,
    es_system_coupling: prevFormData?.es_system_coupling,
    ac_or_dc__c: prevFormData?.ac_or_dc__c,
    es_oem: prevFormData?.es_oem,
    storage_warranty_default: prevFormData?.storage_warranty_default,
    storage_warranty_custom_term: prevFormData?.storage_warranty_custom_term,
    storage_warranty_custom_cost: prevFormData?.storage_warranty_custom_cost,
    storage_additional_proj_diligence_req:
      prevFormData?.storage_additional_proj_diligence_req,
    storage_incentive_program: prevFormData?.storage_incentive_program || [],

    site_lease_upfront_nom: prevFormData?.site_lease_upfront_nom,
    site_lease_solver_type: prevFormData?.site_lease_solver_type || "ongoing",

    dev_budget_override: prevFormData?.dev_budget_override || "No",
    dev_budget_exp_adjust_nom: prevFormData?.dev_budget_exp_adjust_nom,
    dev_only_expense_budget_total_nom_inp:
      prevFormData?.dev_only_expense_budget_total_nom_inp,
    sr_finl_dev_fee_bess_nom: prevFormData?.sr_finl_dev_fee_bess_nom,
    sr_finl_dev_fee_solar_nom: prevFormData?.sr_finl_dev_fee_solar_nom,

    // decommission_bond
    decommission_bond_applies: prevFormData?.decommission_bond_applies || "No",
    decommission_bond_nom: prevFormData?.decommission_bond_nom || 0,

    // removal_fee
    removal_fee_nom: prevFormData?.removal_fee_nom || 0,

    // bess only related fields
    project_type: systemDesignData?.project_type__c,
    epc_solar_per_watt_sd: systemDesignData?.epc_solar_per_watt_sd__c,
    epc_bess_per_kwh_sd: systemDesignData?.epc_bess_per_kwh_sd__c,
    sr_finl_dev_fee_per_kwh_inp:
      prevFormData?.sr_finl_dev_fee_per_kwh_inp || INITIAL_DEV_FEE_BESS,
    capacity_payment_included: prevFormData?.capacity_payment_included || "Yes",
    standalone_storage_avoided_cost:
      prevFormData?.standalone_storage_avoided_cost || 0,
    disc_to_standalone_storage_avoided_cost_inp: decimalPctgToString(
      prevFormData?.disc_to_standalone_storage_avoided_cost_inp,
    ),
    capacity_payment_rate: prevFormData?.capacity_payment_rate || 0,
    capacity_payment_term: prevFormData?.capacity_payment_term || 25,
    capacity_payment_escalator: stringToEscalator(
      prevFormData?.capacity_payment_escalator || "0.0199",
    ),

    // dev fee adjustment
    sr_finl_dev_fee_adj_appl: prevFormData?.sr_finl_dev_fee_adj_appl || "No",
    sr_finl_dev_fee_inv_pv_d_1_grs_nom: prevFormData?.sr_finl_dev_fee_inv_pv_d_1_grs_nom,
    sr_finl_dev_fee_inv_pv_d_2_grs_nom: prevFormData?.sr_finl_dev_fee_inv_pv_d_2_grs_nom,
    sr_finl_dev_fee_inv_pv_d_3_grs_nom: prevFormData?.sr_finl_dev_fee_inv_pv_d_3_grs_nom,
    sr_finl_dev_fee_inv_pv_d_1_adj_nom: prevFormData?.sr_finl_dev_fee_inv_pv_d_1_adj_nom,
    sr_finl_dev_fee_inv_pv_d_2_adj_nom: prevFormData?.sr_finl_dev_fee_inv_pv_d_2_adj_nom,
    sr_finl_dev_fee_inv_pv_d_3_adj_nom: prevFormData?.sr_finl_dev_fee_inv_pv_d_3_adj_nom,
    sr_finl_dev_fee_inv_bess_d_1_grs_nom: prevFormData?.sr_finl_dev_fee_inv_bess_d_1_grs_nom,
    sr_finl_dev_fee_inv_bess_d_2_grs_nom: prevFormData?.sr_finl_dev_fee_inv_bess_d_2_grs_nom,
    sr_finl_dev_fee_inv_bess_d_3_grs_nom: prevFormData?.sr_finl_dev_fee_inv_bess_d_3_grs_nom,
    sr_finl_dev_fee_inv_bess_d_1_adj_nom: prevFormData?.sr_finl_dev_fee_inv_bess_d_1_adj_nom,
    sr_finl_dev_fee_inv_bess_d_2_adj_nom: prevFormData?.sr_finl_dev_fee_inv_bess_d_2_adj_nom,
    sr_finl_dev_fee_inv_bess_d_3_adj_nom: prevFormData?.sr_finl_dev_fee_inv_bess_d_3_adj_nom,

    // minimum dev fee check
    sr_finl_dev_fee_nom_inp: 0,
    skip_minimum_dev_fee_check: prevFormData?.skip_minimum_dev_fee_check || false,
    rate_class: prevFormData?.rate_class,

    greenfield_subtractor: prevFormData?.greenfield_subtractor,

    // use discount or ppa/esa/capacity payment rate as input
    discount_input_type: prevFormData?.discount_input_type || "Discount",
    dollars_kwh_ppa:
      prevFormData?.dollars_kwh_ppa ??
      (systemDesignData?.project_type__c === "Solar-only"
        ? derivedRateFromDiscount
        : undefined),
    dollars_kwh_esa:
      prevFormData?.dollars_kwh_esa ??
      (systemDesignData?.project_type__c === "Solar + Storage"
        ? derivedRateFromDiscount
        : undefined),
  };

  const GRS_DEV_FEE_FIELDS = [
    "sr_finl_dev_fee_inv_pv_d_1_grs_nom",
    "sr_finl_dev_fee_inv_pv_d_2_grs_nom",
    "sr_finl_dev_fee_inv_pv_d_3_grs_nom",
    "sr_finl_dev_fee_inv_bess_d_1_grs_nom",
    "sr_finl_dev_fee_inv_bess_d_2_grs_nom",
    "sr_finl_dev_fee_inv_bess_d_3_grs_nom",
  ] as const;

  const handleSubmit = async (
    formValues: Partial<InputFormPayload> & Record<string, any>,
    type: OptimizationType,
    { setSubmitting, validateForm, setFieldValue },
    sdOverride?: { id: string; data: any },
  ) => {
    validateForm();
    setSubmitting(true);
    // When re-running against a different (e.g. newly created) system design,
    // take the SD data and the SD-derived EPC inputs from that design instead.
    const sd = sdOverride ? sdOverride.data : systemDesignData;
    const sdId = sdOverride ? sdOverride.id : selectedSystemDesign;
    const values: Partial<InputFormPayload> & Record<string, any> = sdOverride
      ? {
          ...formValues,
          epc_total_per_watt_sd:
            sd?.epc_total_per_watt_sd__c ?? formValues.epc_total_per_watt_sd,
          epc_total_per_watt_inp_sd:
            sd?.epc_total_per_watt_sd__c ?? formValues.epc_total_per_watt_inp_sd,
          epc_solar_per_watt_sd:
            sd?.epc_solar_per_watt_sd__c ?? formValues.epc_solar_per_watt_sd,
          epc_bess_per_kwh_sd:
            sd?.epc_bess_per_kwh_sd__c ?? formValues.epc_bess_per_kwh_sd,
        }
      : formValues;
    // dollars_kwh_ppa/dollars_kwh_esa are derived, UI-only helper inputs - never
    // persisted to Salesforce. discount_input_type IS persisted (as
    // discount_input_type__c), so it's intentionally left in the spread below.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { dollars_kwh_ppa, dollars_kwh_esa, ...submittableValues } = values;
    const projectType = sd?.project_type__c || "Solar-only";
    const itcAdders = resolveItcAdders(values, projectType);
    // Quotes always require a system design
    if (!sdId) {
      toast.error("A system design is required to run the pricing model.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      setSubmitting(false);
      return;
    }
    const payload = {
      custom_opp_maintain_cost_escal: "",
      custom_opp_maintain_cost_yr1_per_kw: "",
      custom_opp_maintain_cost_yr6_per_kw: "",
      guaranty_pcnt_sd: "",
      inverter_extended_warr_per_watt: "",
      lease_escal_orig_term: "",
      lease_term_yrs_num: "",
      opportunity_id: opportunityId,
      partner_contract_form_applies:
        opportunityData.partner_contract_form_applies__c,
      esa_escal_orig_term: "",
      esa_term_yrs_num: "",
      ppa_escal_orig_term: "",
      ppa_term_yrs_num: "",
      prepay_nom_cust: 0,
      pricing_model_error: "",
      project_state: opportunityState,
      public_entity: "No",
      rec_type: "",
      s3_link: "",
      sales_channel: opportunityData.sales_channel__c,
      site_lease_annual_nom: "",
      site_lease_escal: "",
      site_lease_term_inp: "",
      solver: "Solver_Discount_PW",
      support_structure_type_primary_qt:
        opportunityData.support_structure_type_primary_qt__c,
      system_design: sdId,
      ...sd,
      ...submittableValues,
      ...itcAdders,
      utility_name: values.utility_name || opportunityData.utility_name__c,
      channel_epc_cost_referral_pcnt:
        prevFormData?.channel_epc_cost_referral_pcnt
          ? prevFormData.channel_epc_cost_referral_pcnt
          : undefined,
      channel_epc_cost_referral_per_watt:
        prevFormData?.channel_epc_cost_referral_per_watt,
      channel_finl_dev_fee_referral_pcnt:
        prevFormData?.channel_finl_dev_fee_referral_pcnt
          ? prevFormData.channel_finl_dev_fee_referral_pcnt
          : undefined,
      referral_partner_epc_cost_referral_pcnt:
        prevFormData?.referral_partner_epc_cost_referral_pcnt
          ? prevFormData.referral_partner_epc_cost_referral_pcnt
          : undefined,
      referral_partner_epc_cost_referral_per_watt:
        prevFormData?.referral_partner_epc_cost_referral_per_watt,
      epc_partner_project_cod_count:
        prevFormData?.epc_partner_project_cod_count,
      referral_partner_project_cod_count:
        prevFormData?.referral_partner_project_cod_count,
      developer_partner_project_cod_count:
        prevFormData?.developer_partner_project_cod_count,
      epc_total_per_watt_sd:
        values.epc_total_per_watt_sd || prevFormData?.epc_total_per_watt_sd,
      epc_total_per_watt_inp_sd:
        values.epc_total_per_watt_sd ||
        prevFormData?.epc_total_per_watt_inp_sd ||
        prevFormData?.epc_total_per_watt_sd,
      // referral partner data
      channel_partner_company_name:
        prevFormData?.channel_partner_company_name || "",
      referral_partner_company_name:
        prevFormData?.referral_partner_company_name || "",
      // battery
      es_system_coupling: sd?.es_system_coupling__c,
      // bess only
      project_type: sd?.project_type__c || "Solar-only",
      pv_installer_cost_nom: sd?.pv_installer_cost_nom__c,
      pv_sunrock_procurement: sd?.pv_sunrock_procurement__c,
      pv_additional_epc_cost_nom: sd?.pv_additional_epc_cost_nom__c,
      bess_installer_cost_nom: sd?.bess_installer_cost_nom__c,
      bess_sunrock_procurement: sd?.bess_sunrock_procurement__c,
      bess_additional_epc_cost_nom: sd?.bess_additional_epc_cost_nom__c,

      // perceentages converted to decimal
      ...(values.disc_to_pre_system_utility_bill_pcnt_inp
        ? {
            disc_to_pre_system_utility_bill_pcnt_inp:
              convertPercentToDecimal(
                values.disc_to_pre_system_utility_bill_pcnt_inp,
              ),
          }
        : {}),
      ...(values.disc_to_standalone_storage_avoided_cost_inp
        ? {
            disc_to_standalone_storage_avoided_cost_inp:
              convertPercentToDecimal(
                values.disc_to_standalone_storage_avoided_cost_inp,
              ),
          }
        : {}),
      ...(values.capacity_payment_escalator
        ? { capacity_payment_escalator: values.capacity_payment_escalator }
        : {}),
      // dev fee adjustment
      sr_finl_dev_fee_adj_appl: values.sr_finl_dev_fee_adj_appl,
      sr_finl_dev_fee_inv_pv_d_1_adj_nom: values.sr_finl_dev_fee_inv_pv_d_1_adj_nom,
      sr_finl_dev_fee_inv_pv_d_2_adj_nom: values.sr_finl_dev_fee_inv_pv_d_2_adj_nom,
      sr_finl_dev_fee_inv_pv_d_3_adj_nom: values.sr_finl_dev_fee_inv_pv_d_3_adj_nom,
      sr_finl_dev_fee_inv_bess_d_1_adj_nom: values.sr_finl_dev_fee_inv_bess_d_1_adj_nom,
      sr_finl_dev_fee_inv_bess_d_2_adj_nom: values.sr_finl_dev_fee_inv_bess_d_2_adj_nom,
      sr_finl_dev_fee_inv_bess_d_3_adj_nom: values.sr_finl_dev_fee_inv_bess_d_3_adj_nom,
    } as QuoteWithSDDTO;

    const data = await optimizeMutation.mutateAsync(
      { data: payload, optimizationType: type, calculatorVersion },
      {
        onError: (error) => {
          console.error("Optimization error:", error);
          toast.warning(
            "Failed to optimize with Error: pricing_model_error. Check the inputs and try again",
            {
              position: toast.POSITION.TOP_RIGHT,
            },
          );
          setShowOutputs(false);
          setSubmitting(false);
        },
      },
    );
    setInputs({
      project_type: sd?.project_type__c,
      ix_delivery_point: sd?.ix_delivery_point__c,
      system_design_name: sd?.Name,
      ...values,
      ...resolveItcAddersForQuote(values, projectType) });
    setOutputs(data.result);
    setOutputDestination(data.path);
    setShowOutputs(true);
    setLastAction(`optimize-${type}`);

    data.result.forEach(record => {
      if (GRS_DEV_FEE_FIELDS.includes(record.data_variable_element) && record.value != null) {
        setFieldValue(record.data_variable_element as string, record.value);
      }
    });
    setSubmitting(false);
  };

  if (devFeeRerunRef) {
    devFeeRerunRef.current = async (sd) => {
      const formik = formikRef.current;
      if (!formik) throw new Error("Calculator form is not ready");
      await handleSubmit(formik.values, "dev", formik, sd);
    };
  }

  const renderField = (field, errors, touched, values, isViewOnly: boolean) => {
    const isDisabled =
      isViewOnly ||
      field.disabled ||
      (field.getDisabled && field.getDisabled(values, systemDesignData));
    const helperNote =
      field.helperNote ||
      (field.getHelperNote && field.getHelperNote(values, systemDesignData));
    const helperNoteAlert =
      field.helperNoteAlert ||
      !!(field.getHelperNoteAlert && field.getHelperNoteAlert(values, systemDesignData));

    switch (field.type) {
      case "select":
        return (
          <SelectorField
            name={field.name}
            label={field.label}
            options={field.options}
            defaultValue={field.defaultValue}
            isViewOnly={isViewOnly}
            isRequired={field.isRequired}
            multiple={field.multiple ?? false}
            isHidden={field.isHidden}
          />
        );
      case "number":
        return (
          <DecimalTextField
            name={field.name}
            label={field.label}
            isPercentage={field.isPercentage}
            withoutDollarSign={field.withoutDollarSign}
            decimals={field.decimals}
            disabled={isDisabled}
            isRequired={field.isRequired}
            helperNote={helperNote}
            helperNoteAlert={helperNoteAlert}
            isTouched={touched[field.name]}
            isHidden={field.isHidden}
            allowNegative={field.allowNegative}
          />
        );
      case "text":
        return (
          <Field
            name={field.name}
            label={field.label}
            disabled={field.disabled || isViewOnly}
            fullWidth
            margin="normal"
            type="text"
            as={TextField}
            sx={{ display: field.isHidden ? 'none' : undefined }}
            InputLabelProps={{
              sx: {
                fontWeight: "bold",
                color: touched[field.name] ? "black" : "primary",
              },
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Card sx={{ maxWidth: 600, margin: "auto" }}>
      <CardContent>
        <Typography variant="h5" component="div" gutterBottom>
          Solar Project Form
        </Typography>
        <LoadingModal show={optimizeMutation.isPending} />
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={() =>
            createValidationSchema(
              currentFormState.current,
              opportunityState,
              opportunityData.utility_name__c,
              systemDesignData,
            )
          }
          onSubmit={() => {}}
          validateOnChange
          validateOnMount
        >
          {({
            errors,
            touched,
            values,
            dirty,
            isSubmitting,
            isValid,
            setSubmitting,
            setFieldValue,
            validateForm,
          }) => {
            currentFormState.current = values;

            useEffect(() => {
              const perWatt = (Number(values.sr_finl_dev_fee_per_watt_inp) || 0) * 1000;
              const perKwh = Number(values.sr_finl_dev_fee_per_kwh_inp) || 0;
              const pvSize = Number(values.pv_size_dc_kw_sd) || 0;
              const batteryKwh = Number(values.battery_energy_rating_kwh) || 0;
              setFieldValue('sr_finl_dev_fee_nom_inp', (perWatt * pvSize + perKwh * batteryKwh).toFixed(2));
            }, [values.sr_finl_dev_fee_per_watt_inp, values.sr_finl_dev_fee_per_kwh_inp, values.pv_size_dc_kw_sd, values.battery_energy_rating_kwh, setFieldValue]);

            useEffect(() => {
              formFields.forEach((field) => {
                if (field.type === "select") {
                  const shouldRender = shouldRenderInput(
                    field,
                    values,
                    opportunityState,
                    opportunityData.utility_name__c,
                    systemDesignData,
                  );
                  if (shouldRender) {
                    if (
                      !field.multiple &&
                      field.options &&
                      field.options.length === 1
                    ) {
                      setFieldValue(field.name, field.options[0].value);
                    }
                  }
                }
              });
            }, [formFields, values, setFieldValue]);

            useEffect(() => {
              if (values.discount_input_type !== "Rate") return;

              const projectType =
                systemDesignData?.project_type__c || values.project_type;
              const setDiscountIfChanged = (
                targetField: string,
                computedPct: number,
              ) => {
                if (isNaN(computedPct) || !isFinite(computedPct)) return;
                const current = parseFloat(
                  String((values as any)[targetField]),
                );
                if (Math.abs((current || 0) - computedPct) > 1e-8) {
                  setFieldValue(targetField, computedPct.toFixed(8));
                }
              };
              const clearDiscountIfSet = (targetField: string) => {
                const current = (values as any)[targetField];
                if (current !== undefined && current !== "") {
                  setFieldValue(targetField, "");
                }
              };

              if (
                projectType === "Solar-only" ||
                projectType === "Solar + Storage"
              ) {
                const rate = parseFloat(
                  String(
                    projectType === "Solar-only"
                      ? values.dollars_kwh_ppa
                      : values.dollars_kwh_esa,
                  ),
                );
                const avoidedCost = parseFloat(
                  String(values.avoided_cost_per_kwh_qt),
                );
                if (!isNaN(rate) && avoidedCost) {
                  setDiscountIfChanged(
                    "disc_to_pre_system_utility_bill_pcnt_inp",
                    (1 - rate / avoidedCost) * 100,
                  );
                } else {
                  clearDiscountIfSet("disc_to_pre_system_utility_bill_pcnt_inp");
                }
              } else if (projectType === "Storage-only") {
                const rate = parseFloat(String(values.capacity_payment_rate));
                const batteryPwrRatingKw = parseFloat(
                  String(
                    systemDesignData?.battery_pwr_rating_kw__c ??
                      values.battery_pwr_rating_kw,
                  ),
                );
                const avoidedCost = parseFloat(
                  String(values.standalone_storage_avoided_cost),
                );
                if (!isNaN(rate) && batteryPwrRatingKw && avoidedCost) {
                  setDiscountIfChanged(
                    "disc_to_standalone_storage_avoided_cost_inp",
                    (1 - (rate * 12 * batteryPwrRatingKw) / avoidedCost) * 100,
                  );
                } else {
                  clearDiscountIfSet(
                    "disc_to_standalone_storage_avoided_cost_inp",
                  );
                }
              }
            }, [
              values.discount_input_type,
              values.dollars_kwh_ppa,
              values.dollars_kwh_esa,
              values.capacity_payment_rate,
              values.avoided_cost_per_kwh_qt,
              values.standalone_storage_avoided_cost,
              setFieldValue,
            ]);

            const optimizedEpcDisabled =
              ((action === "view" ||
                isSubmitting ||
                !dirty ||
                (!isValid && dirty)) &&
                action !== "clone") ||
              !isValid;

            const optimizedDiscountDisabled =
              ((action === "view" ||
                isSubmitting ||
                !dirty ||
                (!isValid && dirty)) &&
                action !== "clone") ||
              !isValid ||
              values.discount_input_type === "Rate";

            const optimizedDiscountHidden =
              systemDesignData.ix_delivery_point__c === "FTM";

            const optimizedSiteLeaseDisabled =
              ((action === "view" ||
                isSubmitting ||
                !dirty ||
                (!isValid && dirty)) &&
                action !== "clone") ||
              !isValid ||
              values.site_lease_included === "No";

            const validSiteLeaseInput = !(
              ((action === "view" ||
                isSubmitting ||
                !dirty ||
                (!isValid && dirty)) &&
                action !== "clone") ||
              !isValid
            );

            return (
              <Form>
                <DevFeeAdjModal
                  show={showDevFeeModal}
                  onHide={() => setShowDevFeeModal(false)}
                  isViewOnly={action === "view"}
                  projectType={values.project_type}
                />
                <Box>
                  {formFields.map((field) => {
                    const isViewOnly =
                      action === "view" ||
                      field.disabled ||
                      (!!field.disabledOnSD && !!selectedSystemDesign);
                    const shouldRender = shouldRenderInput(
                      field,
                      values,
                      opportunityState,
                      opportunityData.utility_name__c,
                      systemDesignData,
                    );
                    if (!shouldRender && values[field.name])
                      setFieldValue(field.name, "");
                    if (field.name === "sr_finl_dev_fee_adj_appl" && shouldRender) {
                      return (
                        <React.Fragment key={field.name}>
                          {renderField(field, errors, touched, values, isViewOnly)}
                          {values.sr_finl_dev_fee_adj_appl === "Yes" && (isDevUser() || isFinanceUser()) &&  (
                            <Button
                              variant="outlined"
                              onClick={() => setShowDevFeeModal(true)}
                              style={{ marginTop: 8, marginBottom: 8 }}
                            >
                              Enter Dev Fee Adjustments
                            </Button>
                          )}
                        </React.Fragment>
                      );
                    }
                    return shouldRender
                      ? renderField(field, errors, touched, values, isViewOnly)
                      : null;
                  })}
                </Box>
                <Box mt={2} display="flex" flex={1} justifyContent="end">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      handleSubmit(values, "discount", {
                        setSubmitting,
                        validateForm,
                        setFieldValue,
                      })
                    }
                    style={{ color: "white" }}
                    disabled={optimizedDiscountDisabled}
                    hidden={optimizedDiscountHidden}
                  >
                    Optimized Discount
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ marginLeft: 10, marginRight: 10, color: "white" }}
                    onClick={() =>
                      handleSubmit(values, "dev", {
                        setSubmitting,
                        validateForm,
                        setFieldValue,
                      })
                    }
                    disabled={
                      ((action === "view" ||
                        isSubmitting ||
                        !dirty ||
                        (!isValid && dirty)) &&
                        action !== "clone") ||
                      !isValid
                    }
                  >
                    Optimized Dev Fee
                  </Button>
                  {
                    <Button
                      variant="contained"
                      color="primary"
                      style={{ color: "white" }}
                      onClick={() =>
                        handleSubmit(values, "epc", {
                          setSubmitting,
                          validateForm,
                          setFieldValue,
                        })
                      }
                      disabled={optimizedEpcDisabled}
                    >
                      Optimized EPC
                    </Button>
                  }
                  {
                    <div
                      title={
                        validSiteLeaseInput && optimizedSiteLeaseDisabled
                          ? "Cannot optimize site lease as Site Lease is set to No"
                          : ""
                      }
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        style={{
                          marginLeft: 10,
                          marginRight: 10,
                          color: "white",
                        }}
                        onClick={() =>
                          handleSubmit(values, "site_lease", {
                            setSubmitting,
                            validateForm,
                            setFieldValue,
                          })
                        }
                        disabled={optimizedSiteLeaseDisabled}
                      >
                        Optimized Site Lease
                      </Button>
                    </div>
                  }
                </Box>
              </Form>
            );
          }}
        </Formik>
      </CardContent>
    </Card>
  );
};

export default CalculatorForm;
